import ButtonPrimary, {
  EButtonSize,
} from "@rentiohq/shared-frontend/dist/components/components/ButtonPrimary";
import { PaymentRequestFetch } from "@rentiohq/shared-frontend/dist/components/components/PaymentRequestFetch";
import { PropertyFetchListItem } from "@rentiohq/shared-frontend/dist/components/components/PropertyFetchListItem";
import { CONFIG } from "@rentiohq/shared-frontend/dist/config/app.config";
import { useCountDown } from "@rentiohq/shared-frontend/dist/hooks/useCountDown";
import logger from "@rentiohq/shared-frontend/dist/logger";
import * as paymentRequestApi from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.api";
import { EPaymentMethod } from "@rentiohq/shared-frontend/dist/types/payment.types";
import {
  EPaymentRequestFeature,
  IPaymentRequest,
} from "@rentiohq/shared-frontend/dist/types/paymentRequest.types";
import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { EInfoHeaderStyle } from "components/InfoHeader/InfoHeader.types";
import withAuthenticationCheck from "hoc/withAuthenticationCheck";
import withKycRequired from "hoc/withKycRequired";
import { default as React } from "react";
import { useParams } from "react-router";
import InfoHeader from "../../../../components/InfoHeader";
import Page from "../../../../components/Page";
import PaymentRequestRedirect from "../../components/PaymentRequestRedirect";
import * as S from "./PaymentLimit.styles";

const PaymentLimitBase = () => {
  const params = useParams<{
    paymentMethod?: EPaymentMethod;
    paymentRequestId: string;
  }>();

  const { remainingCountDownInSeconds } = useCountDown(
    CONFIG.PAYMENT_LIMIT_AMOUNT_MESSAGE_DELAY,
  );

  const [pendingPressSelect, setPendingPressSelect] = React.useState(false);

  // Data
  const paymentRequestId = params.paymentRequestId
    ? parseInt(params.paymentRequestId, 0)
    : undefined;
  const selectedPaymentMethod = params.paymentMethod;

  const callbackUrl = paymentRequestId
    ? `${window.origin}/payment/${paymentRequestId}/status`
    : undefined;

  const getPaymentLimitBtnTitle = () => {
    let title = getLocalizedText("payment_limit_message.button");
    if (remainingCountDownInSeconds) {
      title += ` (${remainingCountDownInSeconds})`;
    }

    return title;
  };

  // Event handlers
  const handlePressSelect = (paymentRequest: IPaymentRequest) => () => {
    if (!selectedPaymentMethod) {
      showAlert({
        type: "info",
        message: getLocalizedText(
          "payment.online.select_method.cta_info.select",
        ),
      });

      return;
    }

    if (!paymentRequestId) {
      return;
    }

    switch (selectedPaymentMethod) {
      case EPaymentMethod.BancontactOrIdeal:
      case EPaymentMethod.Bancontact:
        handlePressWebPayment({
          paymentMethod: EPaymentMethod.Bancontact,
          paymentRequest,
        });
        break;

      case EPaymentMethod.Payconiq:
        handlePressWebPayment({
          paymentMethod: EPaymentMethod.Payconiq,
          paymentRequest,
        });
        break;

      case EPaymentMethod.Ideal:
        handlePressWebPayment({
          paymentMethod: EPaymentMethod.Ideal,
          paymentRequest,
        });
        break;

      default:
        break;
    }
  };

  const handleMollieFlow = async () => {
    if (!paymentRequestId || !callbackUrl) {
      return;
    }

    const { data } = await paymentRequestApi.getMollieUrl(
      paymentRequestId,
      callbackUrl,
    );

    const { redirectUrl } = data;

    window.open(redirectUrl, "_self");
  };

  const handlePaymentWithBanqup = async (paymentMethod: EPaymentMethod) => {
    if (!paymentRequestId || !callbackUrl) {
      return;
    }

    try {
      const { data } = await paymentRequestApi.generateBanqupWebPayment(
        paymentRequestId,
        paymentMethod,
        callbackUrl,
      );

      const { redirectUrl } = data;

      if (!redirectUrl) {
        throw new Error("No redirectUrl or templateUrl found for web payment");
      }

      setTimeout(() => {
        window.open(redirectUrl, "_self");
      }, 20);
      setPendingPressSelect(false);
    } catch (error) {
      showAlert({ error, type: "error" });

      setPendingPressSelect(false);
    }
  };

  const handlePaymentWithMangoPay = async (paymentMethod: EPaymentMethod) => {
    if (!paymentRequestId || !callbackUrl) {
      return;
    }

    const { data } = await paymentRequestApi.generateWebPayment(
      paymentRequestId,
      paymentMethod,
      callbackUrl,
    );

    const { redirectUrl } = data;

    if (!redirectUrl) {
      throw new Error("No redirectUrl or templateUrl found for web payment");
    }

    setTimeout(() => {
      window.open(redirectUrl, "_self");
    }, 20);
  };

  const handlePressWebPayment = async (params: {
    paymentMethod: EPaymentMethod;
    paymentRequest: IPaymentRequest;
  }) => {
    if (!paymentRequestId || !callbackUrl) {
      return;
    }

    const { paymentMethod, paymentRequest } = params;

    setPendingPressSelect(true);

    try {
      // Legacy Mollie flow
      const isLegacyMollieFlow = false;
      if (isLegacyMollieFlow) {
        handleMollieFlow();
        return;
      }

      if (
        paymentRequest?.features.includes(
          EPaymentRequestFeature.BanqupRentDeposit,
        )
      ) {
        handlePaymentWithBanqup(paymentMethod);
        return;
      }

      handlePaymentWithMangoPay(paymentMethod);
    } catch (unknownError) {
      const error = unknownError as any;
      logger.logError({ error });

      showAlert({ error, type: "error" });

      setPendingPressSelect(false);
    }
  };

  // Render
  const renderPaymentLimitMessage = (paymentRequest: IPaymentRequest) => {
    return (
      <PropertyFetchListItem
        shouldRefetch={false}
        renderAlways={() => renderMessage(paymentRequest)}
      />
    );
  };

  const renderMessage = (paymentRequest: IPaymentRequest) => {
    return (
      <Page>
        <InfoHeader
          title={getLocalizedText("payment_limit_message.header")}
          titleStyle={EInfoHeaderStyle.Warning}
          body={getLocalizedText("payment_limit_message.body", {
            paymentMethod: getLocalizedText(
              `payment_request.method.${selectedPaymentMethod}`,
            ),
          })}
        />
        <S.LisWrap>
          <S.ButtonPrimaryWrap>
            <ButtonPrimary
              disabled={remainingCountDownInSeconds > 0}
              title={getPaymentLimitBtnTitle()}
              isLoading={pendingPressSelect}
              onPress={handlePressSelect(paymentRequest)}
              buttonSize={EButtonSize.Full}
            />
          </S.ButtonPrimaryWrap>
        </S.LisWrap>
      </Page>
    );
  };

  if (!paymentRequestId || !selectedPaymentMethod) {
    return null;
  }

  return (
    <>
      <PaymentRequestRedirect id={paymentRequestId} />
      <PaymentRequestFetch
        id={paymentRequestId}
        renderPaymentRequest={renderPaymentLimitMessage}
        paymentRequestV2Enabled={true}
      />
    </>
  );
};

export const PaymentLimit = withAuthenticationCheck(
  withKycRequired(PaymentLimitBase),
);
